import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp } from '../../../styles/animations';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    Chip,
    Box,
    FormGroup,
    Switch,
} from '@mui/material';
import { useQuestionsContext } from '../../../context/QuestionsContext';
import { useToastContext } from '../../../context/ToastContext';
import { usePostHog } from '@posthog/react';
import { isAdmin } from '../../../models/UserHelpers';

const difficultyColors = {
  easy: '#388e3c',
  medium: '#689f38',
  hard: '#f57c00',
  'very hard': '#e64a19',
  impossible: '#d32f2f',
};

const getDifficultyColor = (difficulty) => {
    if (!difficulty) return '#9e9e9e';
    return difficultyColors[difficulty.toLowerCase()] || '#9e9e9e';
};

export default function QuestionCard({
    question,
    onSubmit,
    onNext,
    onPrev,
    currentIndex,
    totalQuestions,
    user,
    hasAnswered,
}) {
    const [userInput, setUserInput] = useState('');
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const { addToast } = useToastContext();
    const { updateQuestion } = useQuestionsContext();
    const posthog = usePostHog();
    const [checked, setChecked] = useState(question.needsReview ?? false);

    const isMC = question.questionType === 'MultipleChoice';

    useEffect(() => {
        setChecked(question.needsReview ?? false);
        // Reset answer state when question changes
        setUserInput('');
        setSelectedOptionId(null);
    }, [question]);

    const handleChangeToggle = (event) => {
        const isChecked = event.target.checked;
        setChecked(isChecked);

        const questionToUpdate = {
            ...question,
            needsReview: isChecked,
            source: question.source?.sourceId || null,
        };

        posthog?.capture('question_review_toggled', {
            question_id: question.questionId,
            difficulty: question.difficulty,
            needs_review: isChecked,
        });

        updateQuestion(questionToUpdate)
            .then(() => {
                addToast('Review Status updated successfully!', 'success');
            })
            .catch((err) => {
                console.error('Error updating question Review Status: ', err);
                addToast('Failed to update Question Review Status.', 'error');
            });
    };

    const handleSubmit = () => {
        const valueToSubmit = isMC ? selectedOptionId : userInput;
        if (!valueToSubmit || (!isMC && !userInput.trim())) return;

        posthog?.capture('question_answered', {
            question_id: question.questionId,
            difficulty: question.difficulty,
            source_id: question.source?.sourceId,
            index: currentIndex,
        });

        onSubmit(valueToSubmit);
        setUserInput('');
        setSelectedOptionId(null);
    };

    const handleNext = () => {
        posthog?.capture('question_navigated', { direction: 'next', index: currentIndex });
        onNext();
    };

    const handlePrev = () => {
        posthog?.capture('question_navigated', { direction: 'prev', index: currentIndex });
        onPrev();
    };

    return (
        <motion.div {...fadeInUp}>
            <Card>
                <CardContent>
                    {/* Source & Difficulty Header */}
                    <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                        flexWrap='wrap'
                        gap={1}
                        mb={2}
                    >
                        {question.source?.title && (
                            <Typography
                                variant='body1'
                                color='#c4a35a '
                                sx={{ fontStyle: 'italic', fontWeight: 600, flex: 1, minWidth: '150px' }}
                            >
                                📚 {question.source.title}
                            </Typography>
                        )}
                        {question.difficulty && (
                            <Chip
                                label={question.difficulty}
                                size='medium'
                                sx={{
                                    fontWeight: 500,
                                    backgroundColor: getDifficultyColor(question.difficulty),
                                    color: 'white',
                                    px: 1.5,
                                    py: 1,
                                }}
                            />
                        )}
                    </Box>

                    {/* Question Text */}
                    <Typography variant='h6' gutterBottom>
                        {question.text}
                    </Typography>

                    {/* Answer Input */}
                    {isMC ? (
                        <Stack spacing={1.5} mt={2}>
                            {question.mcOptions?.map((opt) => (
                                <Button
                                    key={opt.id}
                                    variant='outlined'
                                    onClick={() => setSelectedOptionId(opt.id)}
                                    fullWidth
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        transition: 'all 0.15s ease',
                                        borderWidth: 2,
                                        borderColor: selectedOptionId === opt.id ? '#5cc994' : 'rgba(255,255,255,0.23)',
                                        backgroundColor: 'transparent',
                                        color: selectedOptionId === opt.id ? '#5cc994' : 'text.primary',
                                        fontWeight: selectedOptionId === opt.id ? 700 : 400,
                                        boxShadow: 'none',
                                        transform: selectedOptionId === opt.id ? 'scale(1.01)' : 'scale(1)',
                                        opacity: selectedOptionId && selectedOptionId !== opt.id ? 0.5 : 1,
                                        alignItems: 'flex-start', // pin content to top when text wraps
                                        py: 1.5,
                                        '&:hover': {
                                            borderColor: '#5cc994',
                                            backgroundColor: 'rgba(76,175,130,0.08)',
                                        },
                                    }}
                                >
                                    {/* Radio circle - pinned, never shrinks */}
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            border: '2px solid',
                                            borderColor: selectedOptionId === opt.id ? '#4caf82' : 'rgba(255,255,255,0.4)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            mt: '2px', // slight nudge to align with first line of text
                                            mr: 1.5,
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        {selectedOptionId === opt.id && (
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#ba68c8',
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Letter prefix - also pinned, never shrinks */}
                                    <Typography
                                        component='span'
                                        fontWeight='bold'
                                        sx={{
                                            minWidth: 28,
                                            flexShrink: 0,
                                            mt: '1px',
                                            color: '#ba68c8',
                                        }}
                                    >
                                        {opt.id.toUpperCase()}.
                                    </Typography>

                                    {/* Option text - allowed to wrap */}
                                    <Typography
                                        component='span'
                                        sx={{
                                            textAlign: 'left',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {opt.text}
                                    </Typography>
                                </Button>
                            ))}
                            <Button
                                variant='contained'
                                onClick={handleSubmit}
                                disabled={!selectedOptionId}
                                sx={{ mt: 1 }}
                            >
                                Submit
                            </Button>
                        </Stack>
                    ) : (
                        <Stack direction='row' spacing={2} mt={2}>
                            <TextField
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSubmit();
                                    }
                                }}
                                label='Type your answer'
                                fullWidth
                            />
                            <Button variant='contained' onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Stack>
                    )}

                    {/* Needs Review Toggle */}
                    {isAdmin(user) && (
                        <FormGroup sx={{ mt: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    mt: 2,
                                    width: 'fit-content',
                                }}
                            >
                                <Switch
                                    size='small'
                                    checked={checked}
                                    onChange={handleChangeToggle}
                                    slotProps={{
                                        input: { 'aria-label': 'needs review toggle' },
                                    }}
                                />
                                <Typography
                                    variant='caption'
                                    sx={{
                                        mt: 0.5,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        color: checked ? 'text.secondary' : 'text.disabled',
                                    }}
                                    onClick={() => setChecked((prev) => !prev)}
                                >
                                    Needs Review
                                </Typography>
                            </Box>
                        </FormGroup>
                    )}

                    {/* Navigation */}
                    <Stack direction='row' justifyContent='space-between' mt={3}>
                        <Button
                            onClick={handlePrev}
                            disabled={currentIndex === 0}
                            startIcon={<ChevronLeft />}
                            variant='outlined'
                        >
                            Prev
                        </Button>
                        <Button
                            onClick={handleNext}
                            disabled={!hasAnswered}
                            endIcon={<ChevronRight />}
                            variant='outlined'
                        >
                            Next
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </motion.div>
    );
}